import { setCustomElementMetadata } from "@/lib/customElements";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "../base/button";
import { Checkbox } from "../base/checkbox";
import { Input } from "../base/input";
import { Label } from "../base/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../base/select";
import { Textarea } from "../base/textarea";

import type { ReferralSource } from "@/models";
import { WebsiteIntakeFormService } from "@/services/website-intake-form";
import { GooglePlacesAutocomplete } from "../base/google-places-autocomplete";

const GOOGLE_MAPS_API_KEY = "AIzaSyC4B00aKak8jJOukqaS4kiJDfihw3hsQvM";

// Form schema based on the database schemas and screenshot
const WebsiteIntakeFormSchema = z.object({
  // Contact section
  primaryContactName: z.string().min(1, "Contact name is required"),
  phone: z.string().min(1, "Phone is required"),
  isMobileNumber: z.boolean().default(false),
  contactEmail: z.string().email("Valid email is required"),
  companyName: z.string().optional(),
  title: z.string().optional(),
  officePhone: z.string().optional(),
  extension: z.string().optional(),
  faxNumber: z.string().optional(),
  preferredContactMethod: z.string().optional(),

  // Secondary contact
  addSecondaryContact: z.boolean().default(false),
  secondaryContactName: z.string().optional(),
  secondaryContactTitle: z.string().optional(),
  secondaryContactPhone: z.string().optional(),
  secondaryContactExtension: z.string().optional(),
  secondaryContactEmail: z.string().optional(),

  // Jobsite section
  jobsiteName: z.string().optional(),
  jobAddress: z.string().min(1, "Job address is required"),
  jobCity: z.string().min(1, "Job city is required"),
  jobState: z.string().min(1, "Job state is required"),
  jobZipCode: z.string().min(1, "Job zip code is required"),
  stories: z.string().optional(),
  customerType: z.string().min(1, "Customer type is required"),
  jobsiteContact: z.string().optional(),

  // Billing address
  addBillingAddress: z.boolean().default(false),
  billingAddress: z.string().optional(),
  billingCity: z.string().optional(),
  billingState: z.string().optional(),
  billingZipCode: z.string().optional(),

  // Individual appointment note questions
  requestPurpose: z.string().optional(),
  isPropertyOwner: z.string().optional(),
  tenantContact: z.string().optional(),
  mainContactName: z.string().optional(),
  mainContactEmail: z.string().optional(),
  mainContactPhone: z.string().optional(),
  servicesInterested: z.string().optional(),
  additionalDetails: z.string().optional(),

  // Notes section (this will be auto-generated from individual questions)
  appointmentNotes: z.string().optional(),

  // Other section
  referralSource: z.string().optional(),
});

type FormData = z.infer<typeof WebsiteIntakeFormSchema>;

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const CUSTOMER_TYPES = ["Residential", "Commercial"];

const CONTACT_METHODS = ["Phone", "Email", "Text", "Mail"];

const REQUEST_PURPOSES = [
  "My Primary Residence",
  "A Rental Property",
  "I am a Property Manager",
  "I am a Realtor",
  "I am a General Contractor",
  "Insurance",
  "Other",
];

const YES_NO_OPTIONS = ["Yes", "No"];

export function WebsiteIntakeForm() {
  const [referralSources, setReferralSources] = useState<ReferralSource[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingReferralSources, setIsLoadingReferralSources] =
    useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const apiUrl = "https://gateway.raes.io";
  const apiStage = "dev";

  const websiteIntakeFormService = useMemo(
    () => new WebsiteIntakeFormService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(WebsiteIntakeFormSchema),
    defaultValues: {
      isMobileNumber: false,
      addSecondaryContact: false,
      addBillingAddress: false,
      jobsiteContact: "Same as Primary",
      jobState: "IL", // Default to Illinois based on screenshot
      requestPurpose: "",
      isPropertyOwner: "",
      tenantContact: "",
      mainContactName: "",
      mainContactEmail: "",
      mainContactPhone: "",
      servicesInterested: "",
      additionalDetails: "",
    },
  });

  const addSecondaryContact = watch("addSecondaryContact");
  const addBillingAddress = watch("addBillingAddress");

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingReferralSources(true);
        const referralSources =
          await websiteIntakeFormService.getReferralSources();
        setReferralSources(referralSources);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setIsLoadingReferralSources(false);
      }
    };

    loadData();
  }, [websiteIntakeFormService]);

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      setMessage(null); // Clear any existing message
      const user = JSON.parse(localStorage.getItem("raesUser") || "{}");

      // Combine individual field values into appointmentNotes format
      const appointmentNotes = `What is this request for?
${data.requestPurpose || ""}

Are you the owner of the property?
${data.isPropertyOwner || ""}

Is there a tenant to contact?
${data.tenantContact || ""}

Who is the MAIN point of contact for scheduling (if different from you)?
Name (first, last):
${data.mainContactName || ""}
Email:
${data.mainContactEmail || ""}
Phone:
${data.mainContactPhone || ""}

What services are you interested in?
${data.servicesInterested || ""}

Please provide any additional details for our team (Is it leaking? What type of Roofing Materials are currently installed?, Etc.)
${data.additionalDetails || ""}`;

      // Prepare client data
      const clientData = {
        cuid: parseInt(user.uid),
        cadate: new Date().toISOString().split("T")[0],
        ctype: data.customerType,
        cstatus: "Lead",
        clientstatus: "Qualified",
        ccomp: data.companyName || "",
        cismgmt: "No",
        caddr1: data.addBillingAddress ? data.billingAddress : data.jobAddress,
        caddr2: "",
        ccity: data.addBillingAddress ? data.billingCity : data.jobCity,
        cst: data.addBillingAddress ? data.billingState : data.jobState,
        czip: data.addBillingAddress
          ? parseInt(data.billingZipCode || "0")
          : parseInt(data.jobZipCode),
        cphone: data.officePhone
          ? parseInt(data.officePhone.replace(/\D/g, ""))
          : null,
        caltphone: parseInt(data.phone.replace(/\D/g, "")),
        csmsphone: data.isMobileNumber
          ? parseInt(data.phone.replace(/\D/g, ""))
          : null,
        cext: data.extension || null,
        cfax: data.faxNumber
          ? parseInt(data.faxNumber.replace(/\D/g, ""))
          : null,
        ccontact: data.primaryContactName,
        ctitle: data.title || "",
        cemail: data.contactEmail,
        ccontact2: data.addSecondaryContact ? data.secondaryContactName : null,
        ctitle2: data.addSecondaryContact ? data.secondaryContactTitle : null,
        cphone2:
          data.addSecondaryContact && data.secondaryContactPhone
            ? parseInt(data.secondaryContactPhone.replace(/\D/g, ""))
            : null,
        cext2: data.addSecondaryContact ? data.secondaryContactExtension : null,
        cemail2: data.addSecondaryContact ? data.secondaryContactEmail : null,
        cnotes: appointmentNotes,
        cview: "Y",
        creferral: data.referralSource || "",
        ccontactmeth: data.preferredContactMethod || "",
      };

      // Prepare building data
      const buildingData = {
        badate: new Date().toISOString().split("T")[0],
        bcontact: data.primaryContactName,
        bcontactphone: data.phone,
        bcontactemail: data.contactEmail,
        btype: data.customerType,
        bfloors: data.stories ? parseInt(data.stories) : null,
        bname: data.jobsiteName || "",
        baddr1: data.jobAddress,
        baddr2: "",
        bcity: data.jobCity,
        bst: data.jobState,
        bzip: parseInt(data.jobZipCode),
        bview: "Y",
      };

      // Submit the combined data
      const submissionData = {
        client: clientData,
        building: buildingData,
      };

      // Here you would call your API service to create both client and building
      // For now, we'll just log the data and call onSuccess
      const result = await websiteIntakeFormService.submitWebsiteIntakeForm(
        submissionData
      );
      console.log("Result:", result);

      // Show success message
      setMessage({
        type: "success",
        text: "Form submitted successfully!",
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error("Failed to submit website form:", error);

      // Show error message
      setMessage({
        type: "error",
        text: "Failed to submit form. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingReferralSources) {
    return (
      <div className="flex justify-center items-center p-16 max-w-4xl h-full">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl border-0 shadow-none">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-8"
        noValidate
        id="WebsiteIntakeForm"
      >
        {/* CONTACT Section */}
        <div className="space-y-6">
          <h3 className="pb-2 text-lg font-medium border-b border-gray-200">
            CONTACT
          </h3>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Contact Name */}
            <div className="space-y-2">
              <Label htmlFor="primaryContactName">
                Contact Name <span className="text-red-500">*</span>
              </Label>
              <Input
                {...register("primaryContactName")}
                type="text"
                placeholder="Contact name"
              />
              {errors.primaryContactName && (
                <p className="text-sm text-red-600">
                  {errors.primaryContactName.message}
                </p>
              )}
            </div>
            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">
                Phone <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2 items-center">
                <Input
                  {...register("phone")}
                  placeholder="(000)-000-0000"
                  className="flex-1"
                />
                <div className="flex gap-2 items-center">
                  <Controller
                    name="isMobileNumber"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="isMobileNumber"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <Label htmlFor="isMobileNumber" className="text-sm">
                    Mobile Number?
                  </Label>
                </div>
              </div>
              {errors.phone && (
                <p className="text-sm text-red-600">{errors.phone.message}</p>
              )}
            </div>

            {/* Contact Email */}
            <div className="space-y-2">
              <Label htmlFor="contactEmail">
                Contact Email <span className="text-red-500">*</span>
              </Label>
              <Input
                {...register("contactEmail")}
                type="email"
                placeholder="email@example.com"
              />
              {errors.contactEmail && (
                <p className="text-sm text-red-600">
                  {errors.contactEmail.message}
                </p>
              )}
            </div>

            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="companyName">
                Company Name
              </Label>
              <Input {...register("companyName")} placeholder="Company name" />
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">
                Title
              </Label>
              <Input {...register("title")} placeholder="Job title" />
            </div>

            {/* Office Phone */}
            <div className="space-y-2">
              <Label htmlFor="officePhone">
                Office Phone
              </Label>
              <Input
                {...register("officePhone")}
                placeholder="(000)-000-0000"
              />
            </div>

            {/* Extension */}
            <div className="space-y-2">
              <Label htmlFor="extension">
                Extension
              </Label>
              <Input {...register("extension")} placeholder="000000" />
            </div>

            {/* Fax Number */}
            <div className="space-y-2">
              <Label htmlFor="faxNumber">
                Fax Number
              </Label>
              <Input {...register("faxNumber")} placeholder="(000)-000-0000" />
            </div>

            {/* Preferred Contact Method */}
            <div className="space-y-2">
              <Label
                htmlFor="preferredContactMethod"
               
              >
                Preferred Contact Method
              </Label>
              <Controller
                name="preferredContactMethod"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_METHODS.map((method) => (
                        <SelectItem key={method} value={method}>
                          {method}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          {/* Add Secondary Contact */}
          <div className="flex gap-2 items-center">
            <Controller
              name="addSecondaryContact"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="addSecondaryContact"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label htmlFor="addSecondaryContact">
              Add a Secondary Contact
            </Label>
          </div>

          {/* Secondary Contact Fields */}
          {addSecondaryContact && (
            <div className="grid grid-cols-1 gap-6 p-4 bg-gray-50 rounded-lg md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="secondaryContactName">
                  Secondary Contact Name
                </Label>
                <Input
                  {...register("secondaryContactName")}
                  placeholder="Contact name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondaryContactTitle">
                  Secondary Contact Title
                </Label>
                <Input
                  {...register("secondaryContactTitle")}
                  placeholder="Job title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondaryContactPhone">
                  Secondary Contact Phone
                </Label>
                <Input
                  {...register("secondaryContactPhone")}
                  placeholder="(000)-000-0000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondaryContactExtension">Extension</Label>
                <Input
                  {...register("secondaryContactExtension")}
                  placeholder="000000"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="secondaryContactEmail">
                  Secondary Contact Email
                </Label>
                <Input
                  {...register("secondaryContactEmail")}
                  type="email"
                  placeholder="email@example.com"
                />
              </div>
            </div>
          )}
        </div>

        {/* JOBSITE Section */}
        <div className="space-y-6">
          <h3 className="pb-2 text-lg font-medium border-b border-gray-200">
            JOBSITE
          </h3>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Jobsite Name */}
            <div className="space-y-2">
              <Label htmlFor="jobsiteName">
                Jobsite Name
              </Label>
              <Input {...register("jobsiteName")} placeholder="Jobsite name" />
            </div>

            {/* Job Address */}
            <div className="space-y-2">
              <Label htmlFor="jobAddress">
                Job Address <span className="text-red-500">*</span>
              </Label>
              <GooglePlacesAutocomplete
                value={watch("jobAddress")}
                onChange={(value) => {
                  setValue("jobAddress", value);
                }}
                placeholder="Street address"
                apiKey={GOOGLE_MAPS_API_KEY}
                onLocationSelect={(location) => {
                  setValue("jobAddress", location.addr1 || "");
                  setValue("jobCity", location.city || "");
                  setValue("jobState", location.state || "");
                  setValue("jobZipCode", location.zip || "");
                }}
              />
              {errors.jobAddress && (
                <p className="text-sm text-red-600">
                  {errors.jobAddress.message}
                </p>
              )}
            </div>

            {/* Job City */}
            <div className="space-y-2">
              <Label htmlFor="jobCity">
                Job City <span className="text-red-500">*</span>
              </Label>
              <Input {...register("jobCity")} placeholder="City" />
              {errors.jobCity && (
                <p className="text-sm text-red-600">{errors.jobCity.message}</p>
              )}
            </div>

            {/* Job State */}
            <div className="space-y-2">
              <Label htmlFor="jobState">
                Job State <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="jobState"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.jobState && (
                <p className="text-sm text-red-600">
                  {errors.jobState.message}
                </p>
              )}
            </div>

            {/* Job Zip Code */}
            <div className="space-y-2">
              <Label htmlFor="jobZipCode">
                Job Zip Code <span className="text-red-500">*</span>
              </Label>
              <Input {...register("jobZipCode")} placeholder="Zip code" />
              {errors.jobZipCode && (
                <p className="text-sm text-red-600">
                  {errors.jobZipCode.message}
                </p>
              )}
            </div>

            {/* Stories */}
            <div className="space-y-2">
              <Label htmlFor="stories">
                Stories
              </Label>
              <Input {...register("stories")} placeholder="Number of stories" />
            </div>

            {/* Customer Type */}
            <div className="space-y-2">
              <Label htmlFor="customerType">
                Customer Type <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="customerType"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {CUSTOMER_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.customerType && (
                <p className="text-sm text-red-600">
                  {errors.customerType.message}
                </p>
              )}
            </div>
          </div>

          {/* Add Billing Address */}
          <div className="flex gap-2 items-center">
            <Controller
              name="addBillingAddress"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="addBillingAddress"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label htmlFor="addBillingAddress">
              Add a Billing Address
            </Label>
          </div>

          {/* Billing Address Fields */}
          {addBillingAddress && (
            <div className="grid grid-cols-1 gap-6 p-4 bg-gray-50 rounded-lg md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="billingAddress">Billing Address</Label>
                <Input
                  {...register("billingAddress")}
                  placeholder="Street address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billingCity">Billing City</Label>
                <Input {...register("billingCity")} placeholder="City" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billingState">Billing State</Label>
                <Controller
                  name="billingState"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state.value} value={state.value}>
                            {state.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="billingZipCode">Billing Zip Code</Label>
                <Input {...register("billingZipCode")} placeholder="Zip code" />
              </div>
            </div>
          )}
        </div>

        {/* NOTES Section */}
        <div className="space-y-6">
          <h3 className="pb-2 text-lg font-medium border-b border-gray-200">
            NOTES
          </h3>

          {/* Individual appointment note questions */}
          <div className="space-y-2">
            <Label htmlFor="requestPurpose">
              What is this request for?
            </Label>
            <Controller
              name="requestPurpose"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    {REQUEST_PURPOSES.map((purpose) => (
                      <SelectItem key={purpose} value={purpose}>
                        {purpose}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="isPropertyOwner">
              Are you the owner of the property?
            </Label>
            <Controller
              name="isPropertyOwner"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select yes or no" />
                  </SelectTrigger>
                  <SelectContent>
                    {YES_NO_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tenantContact">
              Is there a tenant to contact?
            </Label>
            <Input
              {...register("tenantContact")}
              placeholder="Enter your response here"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mainContactName">
              Who is the MAIN point of contact for scheduling (if different from
              you)?
            </Label>
            <Input
              {...register("mainContactName")}
              placeholder="Enter the contact's name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mainContactEmail">
              Email:
            </Label>
            <Input
              {...register("mainContactEmail")}
              type="email"
              placeholder="Enter the contact's email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mainContactPhone">
              Phone:
            </Label>
            <Input
              {...register("mainContactPhone")}
              placeholder="(000)-000-0000"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="servicesInterested">
              What services are you interested in?
            </Label>
            <Input
              {...register("servicesInterested")}
              placeholder="Enter your response here"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="additionalDetails">
              Please provide any additional details for our team (Is it leaking?
              What type of Roofing Materials are currently installed?, Etc.)
            </Label>
            <Textarea
              {...register("additionalDetails")}
              placeholder="Enter your response here"
              rows={4}
              className="text-sm"
            />
          </div>
        </div>

        {/* OTHER Section */}
        <div className="space-y-6">
          <h3 className="pb-2 text-lg font-medium border-b border-gray-200">
            OTHER
          </h3>

          {/* Referral Source */}
          <div className="space-y-2">
            <Label htmlFor="referralSource">
              Referral Source
            </Label>
            <Controller
              name="referralSource"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {referralSources.map((referralSource) => (
                      <SelectItem
                        key={referralSource.sid}
                        value={referralSource.source || ""}
                      >
                        {referralSource.source}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-2 justify-end pt-6 border-t">
          <div className="flex gap-2 items-center">
            <AnimatePresence>
              {message && (
                <motion.div
                  initial={{ opacity: 0, x: 20, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{
                    duration: 0.3,
                    type: "spring",
                    stiffness: 200,
                    damping: 20,
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                    message.type === "success"
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : "bg-red-100 text-red-800 border border-red-200"
                  }`}
                >
                  {message.type === "success" ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  {message.text}
                </motion.div>
              )}
            </AnimatePresence>
            <Button
              type="submit"
              disabled={isSubmitting || isLoading || isSubmitted}
              className="bg-[#ff0000] hover:bg-[#ff0000]/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

WebsiteIntakeForm.displayName = "WebsiteIntakeForm";

setCustomElementMetadata({
  component: WebsiteIntakeForm,
  tagName: "raes-website-intake-form",
});
